import { baselineCharacteristicsDataSchema } from "../../../widgets/RJBaselineChars/schemas/dataSchema"
import { outcomeMeasuresDataSchema } from "../../../widgets/RJOutcomeMeasures/schemas/dataSchema"
import { adverseEventsDataSchema } from "../../../widgets/RJAdverseEvents/schemas/dataSchema"

import { COUNTRIES, SHARE_IPD, YES_NO_UNSELECTED } from "../enum/enum"
import { COUNTRY_MAPPING } from "../utils/registryOptionsMapping"

export const primeResultsDataSchema = {
  type: "object",
  properties: {
    results: {
      type: "object",
      properties: {
        more_info: {
          type: "object",
          properties: {
            certain_agreements: {
              type: "object",
              properties: {
                is_pis_employees: {
                  type: "string",
                  enum: ["Yes", "No"],
                },
                results_disclosure: {
                  type: "string",
                  enum: ["Please Select--", "Yes", "No"],
                },
              },
              dependencies: {
                results_disclosure: {
                  oneOf: [
                    {
                      properties: {
                        results_disclosure: {
                          enum: ["Yes"],
                        },
                        pi_disclosure: {
                          type: "string",
                          enum: [
                            "None Selected",
                            " The only disclosure restriction on the PI is that the sponsor can review results communications prior to public release and can embargo communications regarding trial results for a period that is less than or equal to 60 days from the time submitted to the sponsor for review. The sponsor cannot require changes to the communication and cannot extend the embargo.",
                            "The only disclosure restriction on the PI is that the sponsor can review results communications prior to public release and can embargo communications regarding trial results for a period that is more than 60 days but less than or equal to 180 days from the time submitted to the sponsor for review. The sponsor cannot require changes to the communication and cannot extend the embargo.",
                            "Other disclosure agreement that restricts the right of the PI to discuss or publish trial results after the trial is completed.",
                          ],
                        },
                      },
                      dependencies: {
                        pi_disclosure: {
                          oneOf: [
                            {
                              properties: {
                                pi_disclosure: {
                                  enum: [
                                    "Other disclosure agreement that restricts the right of the PI to discuss or publish trial results after the trial is completed.",
                                  ],
                                },
                                describe_agreement: {
                                  type: "string",
                                },
                              },
                            },
                          ],
                        },
                      },
                    },
                  ],
                },
              },
            },
            results_poc: {
              type: "object",
              properties: {
                name: {
                  type: "string",
                },
                organization_name: {
                  type: "string",
                },
                phone: {
                  type: "string",
                },
                ext: {
                  type: "string",
                },
                email: {
                  type: "string",
                },
              },
            },
            global_protocol_amendments: {
              type: "object",
              properties: {
                amendments: {
                  type: "string",
                  enum: [],
                  options: YES_NO_UNSELECTED,
                },
              },
              dependencies: {
                amendments: {
                  oneOf: [
                    {
                      properties: {
                        amendments: {
                          enum: ["Yes"],
                        },
                        add_amendment: {
                          type: "array",
                          items: {
                            type: "object",
                            properties: {
                              amendment_date: {
                                type: "string",
                              },
                              amendment_description: {
                                type: "string",
                              },
                            },
                          },
                        },
                      },
                    },
                  ],
                },
              },
            },
            global_protocol_interruptions: {
              type: "object",
              properties: {
                interruptions: {
                  type: "string",
                  enum: [],
                  options: YES_NO_UNSELECTED,
                  errorMessage:
                    "The question 'Were there any global interruptions to the protocol?' has not been answered. Provide an answer to this question.",
                },
              },
              dependencies: {
                interruptions: {
                  oneOf: [
                    {
                      properties: {
                        interruptions: {
                          enum: ["Yes"],
                        },
                        add_interruption: {
                          type: "array",
                          items: {
                            type: "object",
                            properties: {
                              interruption_date: {
                                type: "string",
                              },
                              interruption_description: {
                                type: "string",
                              },
                              restart: {
                                type: "string",
                              },
                            },
                          },
                        },
                      },
                    },
                  ],
                },
              },
            },
            limitations_caveats: {
              type: "string",
            },
            online_references: {
              type: "object",
              properties: {
                pub_med_identifier: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      link: {
                        type: "string",
                      },
                    },
                  },
                },
              },
            },
          },
        },
        trial_info: {
          title: "Trial Information",
          type: "object",
          properties: {
            trial_id: {
              title: "Trial Identification",
              type: "object",
              properties: {
                eu_num: {
                  type: "string",
                  maxLength: 14,
                },
                reg_num: {
                  type: "string",
                  maxLength: 35,
                },
                official_title: {
                  type: "string",
                  maxLength: 2000,
                },
              },
            },
            additional_study_identifiers: {
              title: "Additional Study Identifiers",
              type: "object",
              properties: {
                isrctn_num: {
                  type: "string",
                  maxLength: 50,
                },
                nct_id: {
                  type: "string",
                  maxLength: 50,
                },
                who_id: {
                  type: "string",
                  maxLength: 50,
                },
              },
            },
            secondary_ids: {
              type: "array",
              items: {
                type: "object",
                onAddButtonTitle: "Add Secondary ID",
                properties: {
                  other_identifier_name: {
                    type: "string",
                    maxLength: 50,
                  },
                  other_identifier_code: {
                    type: "string",
                    maxLength: 50,
                  },
                },
              },
            },
            sponsor: {
              type: "array",
              items: {
                type: "object",
                onAddButtonTitle: "Add Sponsor",
                properties: {
                  name: {
                    type: "string",
                    maxLength: 160,
                  },
                  address: {
                    type: "string",
                    maxLength: 100,
                  },
                  city: {
                    type: "string",
                    maxLength: 100,
                  },
                  zip_code: {
                    type: "string",
                    maxLength: 20,
                  },
                  country: {
                    type: "string",
                    enum: [],
                    options: COUNTRIES,
                    valueMapping: COUNTRY_MAPPING,
                  },
                  scientific_contact: {
                    title: "Scientific Contact Point",
                    type: "object",
                    properties: {
                      name: {
                        type: "string",
                        maxLength: 100,
                      },
                      title: {
                        type: "string",
                        maxLength: 100,
                      },
                      area_code: {
                        type: "string",
                        maxLength: 8,
                      },
                      phone_number: {
                        type: "string",
                        maxLength: 8,
                      },
                      email: {
                        type: "string",
                        maxLength: 100,
                      },
                    },
                  },
                  public_contact: {
                    title: "Public Contact Point",
                    type: "object",
                    properties: {
                      name: {
                        type: "string",
                        maxLength: 100,
                      },
                      title: {
                        type: "string",
                        maxLength: 100,
                      },
                      area_code: {
                        type: "string",
                        maxLength: 8,
                      },
                      phone_number: {
                        type: "string",
                        maxLength: 8,
                      },
                      email: {
                        type: "string",
                        maxLength: 100,
                      },
                    },
                  },
                },
              },
            },
            paediatric_regulatory_details: {
              type: "object",
              title: "",
              properties: {
                trial_part: {
                  type: "string",
                  enum: [],
                  options: YES_NO_UNSELECTED,
                },
              },
              dependencies: {
                trial_part: {
                  oneOf: [
                    {
                      properties: {
                        trial_part: {
                          enum: ["Yes"],
                        },
                        ema_plan: {
                          type: "string",
                        },
                      },
                    },
                    {
                      properties: {
                        trial_part: {
                          enum: ["No"],
                        },
                      },
                    },
                  ],
                },
              },
            },
            article_45: {
              type: "string",
              enum: [],
              options: YES_NO_UNSELECTED,
            },
            article_46: {
              type: "string",
              enum: [],
              options: YES_NO_UNSELECTED,
            },
            result_analysis_stage: {
              type: "object",
              properties: {
                analysis_stage: {
                  type: "string",
                  enum: ["Interim", "Final"],
                },
                analysis_date: {
                  type: "string",
                },
                primary_completion_data: {
                  type: "object",
                  properties: {
                    is_primary_completion_data: {
                      type: "string",
                      enum: [],
                      options: YES_NO_UNSELECTED,
                    },
                  },
                  dependencies: {
                    is_primary_completion_data: {
                      oneOf: [
                        {
                          properties: {
                            is_primary_completion_data: {
                              enum: ["Yes"],
                            },
                            primary_completion_date: {
                              type: "string",
                            },
                          },
                        },
                        {
                          properties: {
                            is_primary_completion_data: {
                              enum: ["No"],
                            },
                          },
                        },
                      ],
                    },
                  },
                },
                global_end_date: {
                  type: "object",
                  properties: {
                    is_trial_date_reached: {
                      type: "string",
                      enum: [],
                      options: YES_NO_UNSELECTED,
                    },
                  },
                  dependencies: {
                    is_trial_date_reached: {
                      oneOf: [
                        {
                          properties: {
                            is_trial_date_reached: {
                              type: "string",
                              enum: ["Yes"],
                            },
                            trial_date: {
                              type: "string",
                            },
                            trial_end: {
                              type: "string",
                              enum: [],
                              options: YES_NO_UNSELECTED,
                            },
                          },
                        },
                        {
                          properties: {
                            is_trial_date_reached: {
                              type: "string",
                              enum: ["No"],
                            },
                          },
                        },
                      ],
                    },
                  },
                },
              },
            },
            general_info: {
              type: "object",
              properties: {
                trial_objectives: {
                  type: "string",
                  maxlength: 1000,
                },
                actual_recruitment_start_date: {
                  type: "string",
                },
                follow_up: {
                  type: "object",
                  properties: {
                    is_planned: {
                      type: "string",
                      enum: [],
                      options: YES_NO_UNSELECTED,
                    },
                  },
                  dependencies: {
                    is_planned: {
                      oneOf: [
                        {
                          properties: {
                            is_planned: {
                              enum: ["Yes"],
                            },
                            rationale: {
                              type: "array",
                              uniqueItems: true,
                              items: {
                                type: "string",
                                enum: [
                                  "Safety",
                                  "Efficacy",
                                  "Ethical reason",
                                  "Regulatory reason",
                                  "Scientific research",
                                ],
                              },
                            },
                            range: {
                              type: "string",
                            },
                            duration_type: {
                              type: "string",
                              enum: ["Months", "Years"],
                            },
                          },
                        },
                      ],
                    },
                  },
                },
                independent: {
                  type: "string",
                  enum: [],
                  options: YES_NO_UNSELECTED,
                },
                protection: {
                  type: "string",
                  maxLength: 2000,
                },
                background: {
                  type: "string",
                  maxLength: 2000,
                },
                evidence: {
                  type: "string",
                  maxLength: 2000,
                },
              },
            },
            population: {
              title: "Population of trial subjects",
              type: "object",
              properties: {
                country_subject_counts: {
                  title: "Subject number per country",
                  enum: [],
                  options: COUNTRIES,
                  valueMapping: COUNTRY_MAPPING,
                  type: "string",
                },
                population_age_group: {
                  title: "Age Breakdown For Trial",
                  type: "string",
                },
              },
            },
          },
        },
        research_information: {
          type: "object",
          properties: {
            study_information: {
              type: "object",
              properties: {
                category_of_study: {
                  type: "string",
                  enum: [],
                  options: [
                    {
                      value: "Company Sponsored Phase 1-3 Study",
                      label: "Company Sponsored Phase 1-3 Study",
                      translations: {
                        jp: "企業治験",
                      },
                    },
                    {
                      value: "Investigator Sponsored Phase 1-3 Study",
                      label: "Investigator Sponsored Phase 1-3 Study",
                      translations: {
                        jp: "医師主導治験",
                      },
                    },
                    {
                      value: "Phase 4 Study",
                      label: "Phase 4 Study",
                      translations: {
                        jp: "製造販売後臨床試験",
                      },
                    },
                    {
                      value: "Post Marketing Surveillance",
                      label: "Post Marketing Surveillance",
                      translations: {
                        jp: "使用成績調査",
                      },
                    },
                    {
                      value: "Others",
                      label: "Others",
                      translations: {
                        jp: "その他",
                      },
                    },
                  ],
                },
                date_of_registration: {
                  type: "string",
                },
                jrct_number: {
                  type: "string",
                },
                official_title: {
                  type: "string",
                },
                brief_title: {
                  type: "string",
                },
                irbs: {
                  type: "string",
                  enum: null,
                },
              },
            },
            summary_of_results: {
              type: "object",
              properties: {
                milestone_dates: {
                  type: "object",
                  properties: {
                    lpo: {
                      type: "object",
                      properties: {
                        baseline: {
                          type: "string",
                        },
                        forecast: {
                          type: "string",
                        },
                        actual: {
                          type: "string",
                        },
                      },
                    },
                  },
                },
                enrollment_number: {
                  type: "string",
                },
                baseline_characteristics: {
                  type: "string",
                },
                participant_flow: {
                  type: "string",
                },
                adverse_events: {
                  type: "string",
                },
                outcome_measures: {
                  type: "string",
                },
                brief_summary: {
                  type: "string",
                },
                anticipated_date_of_disclosure: {
                  type: "string",
                },
                first_journal_publication_date: {
                  type: "string",
                },
                urls_related_to_result: {
                  type: "string",
                },
              },
            },
            ipd_sharing: {
              type: "object",
              properties: {
                plan_to_share_IPD: {
                  type: "string",
                  enum: [],
                  options: SHARE_IPD,
                },
                plan_description: {
                  type: "string",
                },
              },
            },
          },
        },
        participant_flow: {
          title: "Participant Flow",
          type: "object",
          properties: {
            protocol_enrollment: {
              type: "string",
            },
            recruitment_details: {
              type: "string",
            },
            pre_assignment_details: {
              type: "string",
            },
            type_units_analyzed: {
              type: "string",
            },
            participant_flow_table: {
              type: "string",
            },
          },
        },
        baseline_characteristics: baselineCharacteristicsDataSchema,
        outcome_measures: outcomeMeasuresDataSchema,
        adverse_events: adverseEventsDataSchema,
      },
    },
  },
}
